import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Settings, Plus } from 'lucide-react';
import { useWorkspace } from './hooks/useWorkspace';

interface WorkspaceSelectorProps {
  onSelect: (workspaceId: string) => void;
}

export const WorkspaceSelector = ({ onSelect }: WorkspaceSelectorProps) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const { workspaces, status, createWorkspace } = useWorkspace();

  const handleCreate = async () => {
    if (!newWorkspaceName.trim()) {
      setError('Workspace name is required');
      return;
    }
    
    try {
      const workspaceId = await createWorkspace(newWorkspaceName);
      setShowCreateDialog(false);
      setNewWorkspaceName('');
      setError(null);
      if (workspaceId) {
        onSelect(workspaceId);
      }
    } catch (err) {
      setError('Failed to create workspace');
      console.error('Error creating workspace:', err);
    }
  };

  if (status === 'loading') {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Your Workspaces</Typography>
        <Button
          startIcon={<Plus size={16} />}
          variant="contained"
          onClick={() => setShowCreateDialog(true)}
        >
          New Workspace
        </Button>
      </Box>

      {workspaces.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          You don't have any workspaces yet. Create one to get started!
        </Alert>
      ) : (
        <List>
          {workspaces.map((workspace) => (
            <ListItem
              key={workspace.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => onSelect(workspace.id)}
            >
              <ListItemText
                primary={workspace.name}
                secondary={`Created ${new Date(workspace.createdAt).toLocaleDateString()}`}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" aria-label="settings">
                  <Settings size={16} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Workspace</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Workspace Name"
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newWorkspaceName.trim()}
          >
            Create Workspace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};